import type { ActiveUser, DialoguesState } from './models';
import type { IsReadEnum, RecordTypeEnum } from '@/enums';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import {
  dialoguesInfo,
  dialoguesRecordDelete,
  dialoguesRecordList,
  dialoguesSend,
  dialoguesSetRead,
  dialoguesSetRemark,
  dialoguesSetTop,
  getDlInfo,
  getDlSiteList,
  getNotice,
  getUserList,
  getWpExplainV2,
  getWpListSite,
  userCreateSessionChat,
} from '@/api/chat';
import { prompt } from '@/utils/modal';
import { useUserStore } from '../user';

export const useDialoguesStore = defineStore({
  id: 'dialogues',
  state: (): DialoguesState => ({
    hasKey: false,
    dialoguesUserList: [],
    dialoguesUserPage: 1,
    dialoguesUserTotal: 0,
    limit: 20,
    kfMessageNumTime: 0,
    active: 0,
    activeUser: null,
    showUserInfo: false,
    dialoguesRecordList: [],
    dialoguesRecordPage: 1,
    dialoguesRecordTotal: 0,
    quote: null,
    serviceInfo: {
      apiUrl: '',
      siteList: [],
      siteId: 0,
      courierId: 0,
    },
    notice: null,
  }),
  getters: {
    longestWaitDialoguesId(): number {
      const unReadList = this.dialoguesUserList.filter(
        (item: any) => item.no_read_num > 0,
      );
      return unReadList.length > 0 ? unReadList[unReadList.length - 1].id : -1;
    },
    currentSite(): any {
      const userStore = useUserStore();
      if ((userStore as any).isService) {
        if (this.serviceInfo.siteList.length > 0) {
          return this.serviceInfo.siteList.find(
            (item: any) => item.id === this.serviceInfo.siteId,
          );
        }
      }
      return undefined;
    },
    sessionChatSite(): number {
      const [first] = this.dialoguesUserList.filter(
        (item: any) => item.id === this.active,
      );
      return first?.site_id || 0;
    },
    currentSessionChatName(): string {
      const [first] = this.dialoguesUserList.filter(
        (item: any) => item.id === this.active,
      );
      return first ? first.user_name : '';
    },
    siteList(): { value: number, label: string }[] {
      return this.serviceInfo.siteList.map((item: any) => ({
        value: item.id,
        label: item.name,
      }));
    },
    curSite(): number {
      const userStore = useUserStore();
      if ((userStore as any).isService) return 0;
      return this.activeDialoguesUser.site_id;
    },
    courierList(): { value: number, label: string }[] {
      const cs = this.currentSite;
      if (cs) {
        return cs.courierList.map((item: any) => ({
          value: item.id,
          label: item.name,
        }));
      }
      return [];
    },
    ruleList(): any[] {
      const cs = this.currentSite;
      if (cs) {
        const { courierList } = cs;
        if (courierList.length > 0) {
          const currentCourier = courierList.find(
            (item: any) => item.id === this.serviceInfo.courierId,
          );
          if (currentCourier) return currentCourier.ruleList;
          return [];
        }
        return [];
      }
      return [];
    },
    activeDialoguesUser(): any {
      return (
        this.dialoguesUserList.find((item: any) => item.id === this.active)
        || ({} as any)
      );
    },
    dialoguesUnreadNum(): number {
      return this.dialoguesUserList.reduce(
        (acc: number, cur: any) => acc + cur.no_read_num,
        0,
      );
    },
    idActiveDialoguesCanReply(): boolean {
      const userStore = useUserStore();
      if (this.active === 0) return false;
      const user = this.dialoguesUserList.find(
        (item: any) => item.id === this.active,
      );
      if ((userStore as any).isService) return true;
      if (!user) return true;
      return user.site_id > 0;
    },
  },
  actions: {
    async getDialoguesUserList() {
      const { isService } = useUserStore() as any;
      return getUserList().then((res) => {
        this.dialoguesUserList = res;
        if (this.dialoguesUserList.length > 0) {
          if (isService) this.listSort();
        }
      });
    },
    async newSessionChat() {
      return userCreateSessionChat(this.sessionChatSite).then((res: any) => {
        this.dialoguesUserList = this.dialoguesUserList.map((item: any) => {
          if (item.id === this.active) {
            return { ...item, id: res.id };
          }
          return item;
        });
        this.setActive(res.id);
      });
    },
    updateSessionChatId(oldId: number, newId: number) {
      this.dialoguesUserList = this.dialoguesUserList.map((item: any) => {
        return item.id === oldId ? { ...item, id: newId } : item;
      });
    },
    listSort() {
      const sortFunc = (a: any, b: any) => {
        if (b.last_message_time && a.last_message_time) {
          return (
            dayjs(b.last_message_time).unix()
            - dayjs(a.last_message_time).unix()
          );
        }
        else if (b.last_message_time || a.last_message_time) {
          return b.last_message_time ? -1 : 1;
        }
        return 0;
      };

      const topList = this.dialoguesUserList.filter(
        (item: any) => item.is_top === 1,
      );
      const notReadList = this.dialoguesUserList.filter(
        (item: any) => item.is_top === 0 && item.no_read_num > 0,
      );
      const otherList = this.dialoguesUserList.filter(
        (item: any) => item.is_top === 0 && item.no_read_num === 0,
      );
      topList.sort(sortFunc);
      notReadList.sort(sortFunc);
      otherList.sort(sortFunc);
      this.dialoguesUserList = topList.concat(notReadList).concat(otherList);
    },
    async setActive(id: number) {
      this.active = id;
      if (!id) {
        this.dialoguesRecordList = [];
        this.activeUser = {} as ActiveUser;
      }
      else {
        this.getDialoguesRecordList(this.active).then();
        this.getDialoguesUserInfo(this.active).then();
      }
    },
    setUnreadNum(id: number, num: number) {
      this.dialoguesUserList = this.dialoguesUserList.map((item: any) =>
        item.id === id ? { ...item, no_read_num: num } : item,
      );
      const { isService } = useUserStore() as any;
      if (isService) this.listSort();
    },
    async setRead(id: number, isRead: IsReadEnum) {
      return dialoguesSetRead(id, isRead).then(() => {
        this.setUnreadNum(id, isRead);
      });
    },
    addUnreadNum(id: number, time: string) {
      this.dialoguesUserList = this.dialoguesUserList.map((item: any) =>
        item.id === id
          ? {
              ...item,
              no_read_num: item.no_read_num + 1,
              last_message_time: dayjs(Number(time) * 1000).format(
                'YYYY-MM-DD HH:mm:ss',
              ),
            }
          : item,
      );
      const { isService } = useUserStore() as any;
      if (isService) this.listSort();
    },
    async setTop(id: number, isTop: number) {
      await dialoguesSetTop(id.toString(), isTop === 0 ? 1 : 0);
      this.dialoguesUserList = this.dialoguesUserList.map((item: any) =>
        item.id === id
          ? {
              ...item,
              is_top: isTop === 0 ? 1 : 0,
              update_time: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            }
          : item,
      );
      this.listSort();
    },
    updateDialogues(data: any) {
      this.dialoguesUserList.forEach((item: any) => {
        if (item.id === data.id) {
          item.no_read_num = data.no_read_num;
          item.remark = data.remark;
          item.update_time = data.last_message_time;
        }
      });
      const { isService } = useUserStore() as any;
      if (isService) this.listSort();
    },
    addDialogues(data: any, isSelect?: boolean) {
      const { isService, siteId } = useUserStore() as any;
      if (isService && siteId === data.site_id) {
        const ids = this.dialoguesUserList.map((item: any) => item.id);
        if (ids.includes(data.id)) {
          this.dialoguesUserList = this.dialoguesUserList.map((item: any) =>
            item.id === data.id ? data : item,
          );
        }
        else {
          this.dialoguesUserList = [data, ...this.dialoguesUserList];
        }
        if (this.active === 0 || isSelect) {
          this.setActive(data.id).then();
        }
        this.listSort();
      }
    },
    delDialogues(id: number) {
      this.dialoguesUserList = this.dialoguesUserList.filter(
        (item: any) => item.id !== id,
      );
      const [first] = this.dialoguesUserList;
      this.setActive(first ? first.id : 0).then(() => {
        const { isService } = useUserStore() as any;
        if (isService) this.listSort();
      });
    },
    async getDialoguesUserInfo(id: number) {
      const { isService, isClient } = useUserStore() as any;
      if (isClient) {
        return getNotice(this.sessionChatSite).then((res: any) => {
          const noEmpty = Object.keys(res).filter((key: string) => res[key]);
          this.notice = noEmpty.length > 0 ? res : null;
        });
      }
      if (id < 0) {
        this.activeUser = {} as ActiveUser;
        return Promise.resolve();
      }
      const data: any = await dialoguesInfo(id);
      this.activeUser = {} as ActiveUser;
      this.activeUser.id = data.id;
      this.activeUser.userName = data.user_name;
      if (isService) {
        this.activeUser.mobile = data.user_mobile;
        this.activeUser.userId = data.user_id;
        this.activeUser.remark = data.remark;
        switch (data.type) {
          case 'dl':
            this.activeUser.type = '代理';
            this.activeUser.typeNum = 0;
            this.activeUser.appName = data.site_name;
            this.activeUser.website = `https://${data.site_url}`;
            this.activeUser.appUrl = `https://${data.site_url}/sellerSiteApp`;
            break;
          case 'seller':
            this.activeUser.type = '档口';
            this.activeUser.typeNum = 1;
            this.activeUser.appName = data.site_name;
            this.activeUser.website = `https://${data.seller_code}.junpu8.com`;
            this.activeUser.appUrl = `https://www.52dsy.com/shop/${data.seller_id}.html`;
            break;
          case 'dsy':
            this.activeUser.type = '电商园';
            this.activeUser.typeNum = 2;
            this.activeUser.appName = data.site_name;
            this.activeUser.website = data.site_url || '';
            this.activeUser.appUrl = 'https://www.52dsy.com/sellerSiteApp';
            break;
        }
        this.serviceInfo.apiUrl = '';
        this.serviceInfo.siteList = [];
        this.serviceInfo.siteId = 0;
        this.serviceInfo.courierId = 0;
      }
      this.updateDialogues(data);
    },
    async addRemark(id: number, remark: string) {
      const res = await prompt({
        title: '添加备注',
        msg: '输入备注信息',
        inputValue: remark,
      }) as any;
      if (res.action === 'confirm') {
        await dialoguesSetRemark(id, res.value as string).then(() => {
          this.dialoguesUserList = this.dialoguesUserList.map((item: any) => {
            if (item.id === id) {
              return { ...item, remark: res.value };
            }
            return item;
          });
          if (this.activeUser) this.activeUser.remark = res.value as string;
        });
        if (this.active === id) {
          await this.getDialoguesUserInfo(id);
        }
      }
    },
    async getDialoguesRecordList(id: number, prevRecordId?: number) {
      if (id < 0) {
        this.dialoguesRecordList = [];
        return Promise.resolve();
      }
      if (!prevRecordId) this.dialoguesRecordList = [];
      const data: any = await dialoguesRecordList({
        receiver_id: id,
        prev_record_id: prevRecordId,
      });
      const returnList = this.modifyData(data);
      if (!prevRecordId) {
        this.dialoguesRecordList = returnList;
      }
      else {
        this.dialoguesRecordList = this.dialoguesRecordList.concat(returnList);
      }
    },
    setQuote(id: number): void {
      const item = this.dialoguesRecordList.find((item: any) => item.id === id);
      if (item) this.quote = item;
    },
    async sendMessage(id: number, list: [RecordTypeEnum, string][]) {
      return Promise.all(
        list.map(async (item: [RecordTypeEnum, string], index: number) => {
          return await dialoguesSend(
            id,
            item[0],
            item[1],
            index === 0 && this.quote !== null ? this.quote.id : undefined,
          );
        }),
      ).then(async (messages: any[]) => {
        const validMessages = messages
          .reduce(
            (result: any[], message: any) => result.concat(message || []),
            [],
          )
          .filter((message: any) => {
            return message?.id !== undefined
              && message?.receiver_id !== undefined
              && message?.content !== undefined;
          });
        if (validMessages.length > 0) {
          validMessages.forEach((message: any) => {
            this.addMessageToDialoguesList(message);
          });
        }
        else {
          await this.getDialoguesRecordList(id);
        }
        return messages;
      }).finally(() => {
        this.quote = null;
      });
    },
    addMessageToDialoguesList(message: any) {
      const { isClient, role, id } = useUserStore() as any;
      if (message.receiver_id === this.active) {
        const ids = this.dialoguesRecordList.map((item: any) => item.id);
        if (!ids.includes(message.id)) {
          const returnList = this.modifyData([message]);
          this.dialoguesRecordList = returnList.concat(
            this.dialoguesRecordList,
          );
        }
      }
      if (id === message.sender_id && role === message.sender_type) {
        this.setUnreadNum(message.receiver_id, 0);
      }
      else {
        if (message.sender_type === 'dsy') {
          if (isClient)
            this.addUnreadNum(message.receiver_id, message.create_time);
        }
        else {
          this.addUnreadNum(message.receiver_id, message.create_time);
        }
      }
      for (let i = 0; i < this.dialoguesUserList.length; i += 1) {
        if (this.dialoguesUserList[i].id === message.receiver_id) {
          this.dialoguesUserList[i].last_message_time = dayjs(
            Number(message.create_time) * 1000,
          ).format('YYYY-MM-DD HH:mm:ss');
          switch (message.msg_type) {
            case 2:
              this.dialoguesUserList[i].last_message
                = `${message.nickname}：[文件消息]`;
              break;
            case 4:
              this.dialoguesUserList[i].last_message
                = `${message.nickname}：[模板消息]`;
              break;
            default:
              this.dialoguesUserList[i].last_message
                = `${message.nickname}：${message.content}`;
              break;
          }
          return;
        }
      }
    },
    delMessageFromDialoguesList(id: number) {
      this.dialoguesRecordList = this.dialoguesRecordList.filter(
        (item: any) => item.id !== id,
      );
    },
    async delDialoguesItem(dialoguesId: number) {
      return dialoguesRecordDelete(dialoguesId);
    },
    async getSiteList(): Promise<void> {
      if (!this.activeUser) return;
      if (this.serviceInfo.apiUrl === '') {
        const res1: any = await getDlInfo(this.activeUser.website);
        this.serviceInfo.apiUrl = res1.api_url;
      }
      if (this.serviceInfo.siteList.length === 0) {
        const res2: any = await getDlSiteList(this.serviceInfo.apiUrl);
        this.serviceInfo.siteList = res2.map((item: any) => ({
          id: item.site_id,
          name: item.site_name,
          courierList: [],
        }));
        this.serviceInfo.siteId = this.serviceInfo.siteList[0]?.id || 0;
        const res3: any = await getWpListSite(this.serviceInfo.apiUrl, 3, 0);
        this.serviceInfo.siteList.forEach((item: any, index: number) => {
          if (res3[item.id]) {
            item.courierList = res3[item.id].wp_list.map((courier: any) => ({
              id: courier.courier_id,
              name: `${courier.logistics_company}(${courier.yf_between}元)`,
              ruleList: [],
            }));
            if (index === 0) {
              this.serviceInfo.courierId = item.courierList[0]?.id || 0;
            }
          }
        });
      }
    },
    async getRuleList(): Promise<void> {
      const { siteList, siteId, courierId } = this.serviceInfo;
      const siteIndex = siteList.findIndex((item: any) => item.id === siteId);
      if (siteIndex < 0) return;
      let courierIndex = siteList[siteIndex].courierList.findIndex(
        (item: any) => item.id === courierId,
      );
      courierIndex = courierIndex === -1 ? 0 : courierIndex;
      if (
        siteList[siteIndex].courierList[courierIndex]?.ruleList?.length === 0
      ) {
        const res: any = await getWpExplainV2(
          this.serviceInfo.apiUrl,
          siteList[siteIndex].courierList[courierIndex].id,
        );
        siteList[siteIndex].courierList[courierIndex].ruleList = res.rule_list;
      }
    },
    modifyData(data: any[]): any[] {
      return data.map((item: any) => {
        if (item.content && item.content.indexOf('%引用%') >= 0) {
          const [content, quoteContent] = item.content.split('%引用%');
          return {
            ...item,
            content,
            quoteContent,
            time: dayjs(parseInt(item.create_time, 10) * 1000).format(
              'YYYY-MM-DD HH:mm:ss',
            ),
            key: `item.id_${Date.now()}`,
          };
        }
        return {
          ...item,
          time: dayjs(parseInt(item.create_time, 10) * 1000).format(
            'YYYY-MM-DD HH:mm:ss',
          ),
          key: `item.id_${Date.now()}`,
        };
      });
    },
    showUserInfoById(id: number) {
      this.getDialoguesUserInfo(id);
    },
    setKfNum(data: { id: number, num: number }[]) {
      this.dialoguesUserList = this.dialoguesUserList.map((item: any) => {
        const record = data.find((r: any) => r.id === item.site_id);
        if (record) item.kf_num = record.num;
        return item;
      });
    },
  },
});
